import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, KeyRound, Check } from "lucide-react";
import { toast } from "sonner";

const SUGGESTED = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What was your childhood nickname?",
  "What's your mother's maiden name?",
  "What's the name of your first church?",
  "What's the name of the street you grew up on?",
  "What was the first concert you attended?",
  "What's your favorite Bible verse reference (e.g., John 3:16)?",
];

/**
 * Profile card to set the 2 recovery questions. Lets the member pick from
 * suggestions OR type their own. Requires current password as a guard.
 */
export default function SecurityQuestionsCard() {
  const [configured, setConfigured] = useState(false);
  const [q1, setQ1] = useState(SUGGESTED[0]);
  const [a1, setA1] = useState("");
  const [q2, setQ2] = useState(SUGGESTED[1]);
  const [a2, setA2] = useState("");
  const [cur, setCur] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me/security-questions").then(({ data }) => {
      setConfigured(!!data.configured);
      if (data.q1) setQ1(data.q1);
      if (data.q2) setQ2(data.q2);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!a1.trim() || !a2.trim() || !cur) return;
    setBusy(true);
    try {
      await api.post("/auth/me/security-questions", {
        current_password: cur, q1, a1, q2, a2,
      });
      toast.success(configured ? "Recovery questions updated" : "Recovery questions saved");
      setConfigured(true);
      setA1(""); setA2(""); setCur("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7" data-testid="security-questions-card">
      <div className="overline text-burgundy flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Recovery</div>
      <h2 className="font-serif text-2xl text-jjblue mt-1 flex items-center gap-2">
        Security questions
        {configured && <span className="text-[10px] uppercase tracking-wider bg-gold/20 text-burgundy px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Check className="h-3 w-3" /> set</span>}
      </h2>
      <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)] max-w-lg">
        Set 2 questions only you would know the answer to. If you ever lose your password,
        you can reset it by answering them — no email access needed.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-4 max-w-lg">
        {[1, 2].map((n) => {
          const q = n === 1 ? q1 : q2;
          const setQ = n === 1 ? setQ1 : setQ2;
          const a = n === 1 ? a1 : a2;
          const setA = n === 1 ? setA1 : setA2;
          return (
            <div key={n} className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-burgundy">Question {n}</label>
              <select className="input-jj" value={SUGGESTED.includes(q) ? q : "__custom__"} onChange={(e) => setQ(e.target.value === "__custom__" ? "" : e.target.value)} data-testid={`sq-q${n}-pick`}>
                {SUGGESTED.map((s) => <option key={s}>{s}</option>)}
                <option value="__custom__">Write my own…</option>
              </select>
              {!SUGGESTED.includes(q) && (
                <input className="input-jj" placeholder="Type your question" value={q} onChange={(e) => setQ(e.target.value)} maxLength={160} data-testid={`sq-q${n}-text`} />
              )}
              <input
                type="text"
                className="input-jj"
                placeholder={`Answer to question ${n}`}
                value={a}
                onChange={(e) => setA(e.target.value)}
                required
                maxLength={160}
                data-testid={`sq-a${n}`}
              />
            </div>
          );
        })}
        <div>
          <label className="text-xs uppercase tracking-wider text-burgundy">Confirm with current password</label>
          <input type="password" className="input-jj mt-1" value={cur} onChange={(e) => setCur(e.target.value)} required minLength={6} autoComplete="current-password" data-testid="sq-current-pw" />
        </div>
        <button type="submit" disabled={busy} className="btn-primary !py-2.5 !px-5 text-sm inline-flex items-center gap-1.5" data-testid="sq-submit">
          <KeyRound className="h-3.5 w-3.5" /> {busy ? "Saving…" : (configured ? "Update questions" : "Save questions")}
        </button>
      </form>
    </div>
  );
}
